import axios, { AxiosError } from 'axios'
import {
  AvailabilitySlot,
  BookingInterface,
  EventInterface,
} from 'common/components/entities/Calendar/calendar-interface'
import { EventTimeZoneTypeEnum } from 'common/components/entities/Calendar/constants'
import { useEvent } from 'common/components/entities/Calendar/hooks/use-event'
import {
  getStartEndDates,
  joinDateWithTimeSlot,
} from 'common/components/entities/Calendar/utils'
import { getLocalTimeZone } from 'common/utils/date-utils'
import { getUrlPathList } from 'common/utils/urlUtils'
import {
  GetTimeSlotsArgs,
  getEvent,
  getTimeSlots,
  rescheduleBooking,
} from 'publisher/api/booking-calendar-api'
import { useBooking } from 'publisher/components/entities/booking-calendar/use-booking'
import { useEffect, useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'

export const useRescheduleBooking = () => {
  const [selectedStartDate, setSelectedStartDate] = useState('')
  const [selectedEndDate, setSelectedEndDate] = useState('')
  const [selectedDate, setSelectedDate] = useState<Date>()
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<number>()
  const [selectedTimeZone, setSelectedTimeZone] = useState(getLocalTimeZone())
  const [selectedLocationId, setSelectedLocationId] = useState<number>()
  const { t } = useTypedTranslation()

  // https://${domain_name}/${locale}/booking-calendar/${token}/reschedule
  // todo: we should refactor it, we shouldn't use window there
  const token = getUrlPathList(
    typeof window !== 'undefined' ? window.location.href : '',
  )[2]

  const { booking, isFetching } = useBooking(token, selectedTimeZone)
  const { timeSlots = [], isFetching: isTimeSlotsFetching } =
    useRescheduleBookingTimeSlots({
      eventId: booking?.eventId,
      bookingId: booking?.id,
      ownerId: booking?.ownerId,
      timeZone: selectedTimeZone,
      startDate: selectedStartDate,
      endDate: selectedEndDate,
    })

  const { event, isFetching: isEventFetching } = useEvent({
    eventId: booking?.eventId,
    fetcher: getEvent,
  })

  useEffect(() => {
    if (booking?.eventLocationId) {
      setSelectedLocationId(booking.eventLocationId)
    }
  }, [booking?.eventLocationId])

  useEffect(() => {
    if (event) {
      setSelectedTimeZone(
        event.displayTimezoneType === EventTimeZoneTypeEnum.Local
          ? getLocalTimeZone()
          : event.displayTimezone || getLocalTimeZone(),
      )
    }
  }, [event])

  const onMonthSelect = (month: Date) => {
    const { startDate, endDate } = getStartEndDates(month)
    setSelectedStartDate(startDate)
    setSelectedEndDate(endDate)
  }

  const [isLoading, setIsLoading] = useState(false)
  const [apiErrors, setApiErrors] = useState<string[]>([])
  const [isRescheduled, setIsRescheduled] = useState(false)

  const handleRescheduleBooking = async () => {
    try {
      setIsLoading(true)
      setApiErrors([])
      await rescheduleBooking({
        timezone: selectedTimeZone,
        token,
        bookingId: booking!.id,
        date: joinDateWithTimeSlot(selectedDate, selectedTimeSlot),
      })
      setIsRescheduled(true)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const data = (
          error as AxiosError<{ violations?: Array<{ message?: string }> }>
        ).response?.data
        const messages =
          data?.violations
            ?.map(v => v.message)
            .filter(
              (m): m is string => typeof m === 'string' && m.trim() !== '',
            ) ?? []

        setApiErrors(
          messages.length
            ? messages
            : [t('core.errors.service_temporary_unavailable')],
        )
        return
      }

      setApiErrors([t('core.errors.service_temporary_unavailable')])
    } finally {
      setIsLoading(false)
    }
  }

  return {
    selectedDate,
    selectedTimeSlot,
    selectedTimeZone,
    selectedLocationId,
    setSelectedDate,
    setSelectedTimeSlot,
    setSelectedTimeZone,
    setSelectedLocationId,
    event,
    booking,
    isEventFetching,
    timeSlots,
    isTimeSlotsFetching,
    onMonthSelect,

    handleRescheduleBooking,
    isLoading: isFetching || isLoading,
    apiErrors,
    isRescheduled,
  }
}

interface useRescheduleBookingTimeSlotsParams {
  eventId?: EventInterface['id']
  bookingId?: BookingInterface['id']
  ownerId?: EventInterface['ownerId']
  startDate: string
  endDate: string
  timeZone: string
}

function useRescheduleBookingTimeSlots({
  eventId,
  bookingId,
  ownerId,
  startDate,
  endDate,
  timeZone,
}: useRescheduleBookingTimeSlotsParams) {
  const [timeSlots, setTimeSlots] = useState<AvailabilitySlot[]>()
  const [error, setError] = useState<unknown>(null)
  const [isFetching, setIsFetching] = useState(false)

  const fetch = async (args: GetTimeSlotsArgs) => {
    try {
      setIsFetching(true)
      const res = await getTimeSlots(args)
      setTimeSlots(res.data)
    } catch (error) {
      setError(error)
    } finally {
      setIsFetching(false)
    }
  }

  useEffect(() => {
    if (eventId && ownerId) {
      const defaultMonth = getStartEndDates(new Date())
      fetch({
        eventId,
        startDate: startDate || defaultMonth.startDate,
        endDate: endDate || defaultMonth.endDate,
        timezone: timeZone,
        bookingIdToBeRescheduled: bookingId,
        ownerId,
      })
    }
  }, [eventId, ownerId, timeZone, startDate, endDate])

  return { timeSlots, isFetching, error, fetch }
}

export const useRescheduleBookingErrors = ({
  selectedDate,
  selectedTimeSlot,
}: {
  selectedDate?: Date
  selectedTimeSlot?: number
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isErrorsVisible, setIsErrorsVisible] = useState(false)
  const { t } = useTypedTranslation()

  const validate = () => {
    let updatedErrors: typeof errors = {}
    if (!selectedDate) {
      updatedErrors = {
        selectedDate: t('entities.calendar.date_field_not_empty'),
      }
    }
    if (typeof selectedTimeSlot !== 'number') {
      updatedErrors = {
        ...updatedErrors,
        timeSlot: t('entities.calendar.time_slot_field_not_empty'),
      }
    }
    setErrors(updatedErrors)
  }

  useEffect(() => {
    validate()
  }, [selectedDate, selectedTimeSlot])

  return { errors: Object.values(errors), isErrorsVisible, setIsErrorsVisible }
}
